import { useState, useEffect, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Form,
  FormGroup,
  Input,
  InputGroup,
  InputGroupText,
  Label,
  Spinner,
} from "reactstrap";
import { Link } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { Button, Stack } from "react-bootstrap";
import { HugeiconsIcon } from "@hugeicons/react";
import ReCAPTCHA from "react-google-recaptcha";
import {
  Mail01Icon,
  SquareLock01Icon,
  ViewIcon,
  ViewOffSlashIcon,
} from "@hugeicons/core-free-icons";
import { P } from "../AbstractElements";
import { useAuthContext } from "../_helper/Auth";
import {
  readPasswordChangeRequiredFromPayload,
  readEmailVerificationFromPayload,
} from "../utils/authPayloadGates";
import { useAuthMutation } from "../services/useAuth/useAuthMutation";
import { AntInput } from "../CommonElements/AntInput";
import { Space } from "antd";

export default function SignInForm() {
  const isDevelopment = process.env.NODE_ENV === "development";
  // Form setup with react-hook-form
  const defaultValues =
      isDevelopment ? {
        defaultValues: {
          email: "khaledmuhammad.dev@gmail.com",
          // password: "123@#$Ali",
          password: "KD@IPMAC@1447#Dev",
          remember_me: true,
        },
      }
      : {};
  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm({
    ...defaultValues,
  });

  // ReCAPTCHA state
  const [recaptchaValue, setRecaptchaValue] = useState(null);
  const [recaptchaError, setRecaptchaError] = useState("");

  // reCAPTCHA loading state
  const [isRecaptchaLoaded, setIsRecaptchaLoaded] = useState(false);
  const [recaptchaLoadError, setRecaptchaLoadError] = useState(false);
  const recaptchaRef = useRef(null);
  const [retryCount, setRetryCount] = useState(0);

  // Error state for API errors
  const [apiError, setApiError] = useState("");

  const {
    addToken,
    addUser,
    setEmailVerificationPending,
    setPasswordChangeRequired,
  } = useAuthContext();
  const { t } = useTranslation();
  const { loginMutation } = useAuthMutation();

  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const toggle = () => {
    setIsPasswordVisible(!isPasswordVisible);
  };

  // Load reCAPTCHA script only on sign-in page (avoids timeout errors on other pages e.g. reports)
  useEffect(() => {
    let timeoutId;
    const RECAPTCHA_LOAD_TIMEOUT = 15000;

    const markReady = () => {
      setIsRecaptchaLoaded(true);
      setRecaptchaLoadError(false);
    };

    const markError = () => {
      setRecaptchaLoadError(true);
      setIsRecaptchaLoaded(false);
    };

    const initRecaptcha = () => {
      if (!window.grecaptcha || typeof window.grecaptcha.ready !== "function") {
        timeoutId = setTimeout(markError, RECAPTCHA_LOAD_TIMEOUT);
        return;
      }
      const readyPromise = window.grecaptcha.ready(markReady);
      if (readyPromise && typeof readyPromise.catch === "function") {
        readyPromise.catch(() => markError());
      }
    };

    if (document.querySelector('script[src*="google.com/recaptcha"]')) {
      initRecaptcha();
      return () => clearTimeout(timeoutId);
    }

    const script = document.createElement("script");
    script.src = "https://www.google.com/recaptcha/api.js";
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.grecaptcha && typeof window.grecaptcha.ready === "function") {
        const readyPromise = window.grecaptcha.ready(markReady);
        if (readyPromise && typeof readyPromise.catch === "function") {
          readyPromise.catch(() => markError());
        }
      } else {
        timeoutId = setTimeout(markError, RECAPTCHA_LOAD_TIMEOUT);
      }
    };
    script.onerror = markError;
    document.head.appendChild(script);

    return () => clearTimeout(timeoutId);
  }, [retryCount]);

  const handleRetryRecaptcha = () => {
    setRetryCount((prev) => prev + 1);
    setRecaptchaLoadError(false);
    setIsRecaptchaLoaded(false);
    setRecaptchaValue(null);

    // Force reload the recaptcha widget
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
  };

  const handleRecaptchaChange = (value) => {
    setRecaptchaValue(value);
    setRecaptchaError("");
  };

  const handleRecaptchaExpired = () => {
    setRecaptchaValue(null);
    setRecaptchaError(t("RECAPTCHA_EXPIRED"));
  };

  const onSubmit = (data) => {
    // Reset any previous errors
    setRecaptchaError("");
    setApiError("");

    if (!recaptchaValue && !isDevelopment) {
      setRecaptchaError(t("RECAPTCHA_ERROR"));
      return;
    }

    loginMutation.mutate(data, {
      onSuccess: (response) => {
        const d = response.data;
        addToken({
          access_token: d.access_token,
          refresh_token: d.refresh_token,
        });
        addUser(d.user);
        setEmailVerificationPending(readEmailVerificationFromPayload(d) ?? false);
        setPasswordChangeRequired(readPasswordChangeRequiredFromPayload(d) ?? false);
      },
      onError: (err) => {
        if (window.grecaptcha) {
          window.grecaptcha.reset();
        }
        setRecaptchaValue(null);

        // Handle 401 Unauthorized error
        if (err.response?.status === 401) {
          setApiError(t("UNAUTHORIZED_ERROR"));
        } else {
          // Handle other errors
          setApiError(
            t("LOGIN_ERROR") ||
            "An error occurred during login. Please try again."
          );
        }
      },
    });
  };

  return (
    <div className="login-card1 decorative-arabic-text-bg">
      <Form
        onSubmit={handleSubmit(onSubmit)}
        className="theme-form login-form pattern-card rounded-3 border border-1"
      >
        <Stack gap={3}>
          <h4>{t("LOGIN")}</h4>
          <h6>{t("LOGIN_DESCRIPTION")}</h6>
        </Stack>

        {/* Error Message Component */}
        {apiError && (
          <div className="alert alert-danger-outline text-danger gap-2 border border-danger rounded-3 mb-3 d-flex align-items-center">
            <div className="me-2">
              <HugeiconsIcon
                icon={SquareLock01Icon}
                style={{ color: "#dc3545", fontSize: "18px" }}
              />
            </div>
            <div className="flex-grow-1">
              <strong className="d-block mb-1">
                {t("LOGIN_FAILED") || "Login Failed"}
              </strong>
              <small className="text-black h5">{apiError}</small>
            </div>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={() => setApiError("")}
              aria-label="Close"
            ></button>
          </div>
        )}

        <FormGroup>
          <Label>{t("EMAIL_ADDRESS")}</Label>
          <Controller
            name="email"
            control={control}
            rules={{
              required: t("EMAIL_REQUIRED") || "Email is required",
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message:
                  t("EMAIL_INVALID") || "Please enter a valid email address",
              },
            }}
            render={({ field }) => (
              <InputGroup>
                <InputGroupText>
                  <HugeiconsIcon icon={Mail01Icon} />
                </InputGroupText>
                <AntInput
                  {...field}
                  type="email"
                  status={errors.email ? "error" : ""}
                  placeholder={t("EMAIL_ADDRESS")}
                />
              </InputGroup>
            )}
          />
          {errors.email && (
            <div className="text-danger small mt-1">{errors.email.message}</div>
          )}
        </FormGroup>

        <FormGroup>
          <Label>{t("PASSWORD")}</Label>
          <Controller
            name="password"
            control={control}
            rules={{
              required: t("PASSWORD_REQUIRED") || "Password is required",
              minLength: {
                value: 6,
                message:
                  t("PASSWORD_MIN_LENGTH") ||
                  "Password must be at least 6 characters",
              },
            }}
            render={({ field }) => (
              <InputGroup className="position-relative">
                <InputGroupText>
                  <HugeiconsIcon icon={SquareLock01Icon} />
                </InputGroupText>
                <AntInput
                  {...field}
                  type={isPasswordVisible ? "text" : "password"}
                  placeholder={t("PASSWORD")}
                  status={errors.password ? "error" : ""}
                />
                <div role="button" className="show-hide" onClick={toggle}>
                  {!isPasswordVisible ? (
                    <HugeiconsIcon icon={ViewIcon} />
                  ) : (
                    <HugeiconsIcon icon={ViewOffSlashIcon} />
                  )}
                </div>
              </InputGroup>
            )}
          />
          {errors.password && (
            <div className="text-danger small mt-1">
              {errors.password.message}
            </div>
          )}
        </FormGroup>

        <FormGroup>
          <Space>
            <Controller
              name="remember_me"
              control={control}
              render={({ field }) => (
                <Input
                  id="remember_me"
                  name="remember_me"
                  type="checkbox"
                  {...field}
                />
              )}
            />
            <Label for="remember_me">{t("REMEMBER_ME")}</Label>
          </Space>
        </FormGroup>

        <FormGroup>
          {!isRecaptchaLoaded ? (
            <div className="d-flex align-items-center gap-2">
              <Spinner size="sm" />
              <small className="text-muted">
                {t("LOADING_RECAPTCHA") || "Loading reCAPTCHA..."}
              </small>
            </div>
          ) : recaptchaLoadError ? (
            <div className="alert alert-warning">
              <div className="d-flex align-items-center gap-2">
                <HugeiconsIcon
                  icon={SquareLock01Icon}
                  style={{ fontSize: "18px" }}
                />
                <div className="flex-grow-1">
                  <strong className="d-block">
                    {t("RECAPTCHA_LOAD_ERROR") || "Failed to load reCAPTCHA"}
                  </strong>
                  <small>
                    {t("RECAPTCHA_LOAD_ERROR_DESC") ||
                      "Please check your connection or disable ad blockers"}
                  </small>
                </div>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={handleRetryRecaptcha}
                >
                  {t("RETRY") || "Retry"}
                </Button>
              </div>
            </div>
          ) : (
            <>
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                onChange={handleRecaptchaChange}
                onExpired={handleRecaptchaExpired}
              />
              {recaptchaError && (
                <div className="text-danger small mt-1">{recaptchaError}</div>
              )}
            </>
          )}
        </FormGroup>

        <FormGroup className="login-btn">
          <Link to="/forgot-password" className="link fw-bold">
            {t("FORGOT_PASSWORD")}
          </Link>
        </FormGroup>

        <FormGroup>
          <Button
            disabled={loginMutation.isPending || !isValid}
            type="submit"
            onClick={() => { }}
            variant="primary"
            size="lg"
            className="w-100"
          >
            <Stack
              direction="horizontal"
              gap={2}
              className="justify-content-center"
            >
              {loginMutation.isPending && <Spinner size="sm" />}
              {t("SIGN_IN")}
            </Stack>
          </Button>
          <P attrPara={{ className: "mt-3" }}>
            <Trans
              i18nKey="SIGNNING_IN_TERMS"
              components={{
                termsLink: <a href="/terms" className="font-primary">{t("TERMS_AND_CONDITIONS")}</a>,
              }}
            />
          </P>
        </FormGroup>
      </Form>
    </div>
  );
}
